import { useState, useEffect, useRef } from "react";

const css = `
@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;700;900&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; }

.ld-screen {
  position: fixed; inset: 0; z-index: 9999;
  background: #050d1a;
  display: flex; align-items: center; justify-content: center;
  direction: rtl; font-family: 'Heebo', sans-serif;
  overflow: hidden;
  transition: opacity .7s ease, visibility .7s ease;
}
.ld-screen.hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* ── VIDEO BG ── */
.ld-video {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  opacity: 0.35;
}

/* ── DARK OVERLAY ── */
.ld-overlay {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse at center, rgba(5,13,26,.4) 0%, rgba(5,13,26,.92) 100%);
}

/* ── CONTENT ── */
.ld-content {
  position: relative; z-index: 2;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 0;
  text-align: center;
}

/* ── LOGO RING ── */
.ld-ring {
  position: relative;
  width: 110px; height: 110px;
  margin-bottom: 28px;
}
.ld-ring__circle {
  position: absolute; inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #c9a84c;
  border-right-color: rgba(201,168,76,.3);
  animation: ld-spin 1.4s linear infinite;
}
.ld-ring__circle2 {
  position: absolute; inset: 8px;
  border-radius: 50%;
  border: 1px solid transparent;
  border-bottom-color: rgba(201,168,76,.5);
  animation: ld-spin 2s linear infinite reverse;
}
.ld-ring__inner {
  position: absolute; inset: 16px;
  border-radius: 50%;
  background: rgba(201,168,76,.08);
  border: 1px solid rgba(201,168,76,.2);
  display: flex; align-items: center; justify-content: center;
}
.ld-ring__logo {
  font-size: 11px; font-weight: 900; letter-spacing: 1px;
  background: linear-gradient(135deg, #fff, #e4c97e, #c9a84c);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2; text-align: center;
}

@keyframes ld-spin {
  to { transform: rotate(360deg); }
}

/* ── BRAND NAME ── */
.ld-brand {
  font-size: clamp(32px, 6vw, 52px);
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #ffffff 0%, #e4c97e 50%, #c9a84c 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  animation: ld-fadein .8s ease .2s both;
}
.ld-brand span {
  background: linear-gradient(135deg, #c9a84c, #a87c2a);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}

/* ── TAGLINE ── */
.ld-tagline {
  font-size: 13px; font-weight: 400; color: rgba(255,255,255,.45);
  letter-spacing: 3px; text-transform: uppercase; margin-bottom: 40px;
  animation: ld-fadein .8s ease .4s both;
}

/* ── PROGRESS BAR ── */
.ld-bar-wrap {
  width: 200px; height: 2px;
  background: rgba(255,255,255,.08);
  border-radius: 2px; overflow: hidden;
  margin-bottom: 14px;
  animation: ld-fadein .5s ease .5s both;
}
.ld-bar {
  height: 100%; border-radius: 2px;
  background: linear-gradient(90deg, #c9a84c, #e4c97e);
  box-shadow: 0 0 8px rgba(201,168,76,.6);
  transition: width .15s ease;
}

/* ── PERCENT ── */
.ld-pct {
  font-size: 12px; font-weight: 700; color: rgba(201,168,76,.7);
  letter-spacing: 1px;
  animation: ld-fadein .5s ease .5s both;
}

@keyframes ld-fadein {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── CORNER DECORATIONS ── */
.ld-corner {
  position: absolute; width: 40px; height: 40px;
  opacity: .25;
}
.ld-corner--tl { top: 24px; right: 24px; border-top: 1.5px solid #c9a84c; border-right: 1.5px solid #c9a84c; }
.ld-corner--tr { top: 24px; left:  24px; border-top: 1.5px solid #c9a84c; border-left:  1.5px solid #c9a84c; }
.ld-corner--bl { bottom: 24px; right: 24px; border-bottom: 1.5px solid #c9a84c; border-right: 1.5px solid #c9a84c; }
.ld-corner--br { bottom: 24px; left:  24px; border-bottom: 1.5px solid #c9a84c; border-left:  1.5px solid #c9a84c; }

/* ── GOLD PARTICLES ── */
.ld-particles {
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
}
.ld-dot {
  position: absolute; width: 2px; height: 2px;
  border-radius: 50%; background: #c9a84c;
  animation: ld-float var(--d,4s) ease-in-out var(--delay,0s) infinite;
  opacity: 0;
}
@keyframes ld-float {
  0%   { opacity: 0; transform: translateY(0) scale(1); }
  20%  { opacity: .6; }
  80%  { opacity: .3; }
  100% { opacity: 0; transform: translateY(-120px) scale(0.5); }
}
`;

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  top: `${40 + Math.random() * 50}%`,
  d: `${3 + Math.random() * 4}s`,
  delay: `${Math.random() * 4}s`,
}));

const TOTAL_MS = 2000; // 2 seconds total
const TICK_MS = 50;   // update every 50ms

export default function LoadingScreen({ videoSrc, onDone }) {
  const [progress, setProgress] = useState(0);
  const [hidden, setHidden] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += TICK_MS;
      // ease-out curve: fast at start, decelerates near 100
      const raw = elapsed / TOTAL_MS;           // 0 → 1
      const eased = 1 - Math.pow(1 - raw, 2);  // quadratic ease-out
      const pct = Math.min(Math.round(eased * 100), 100);
      setProgress(pct);

      if (elapsed >= TOTAL_MS) {
        clearInterval(timer);
        setTimeout(() => {
          setHidden(true);
          setTimeout(() => onDone?.(), 700);
        }, 150);
      }
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [onDone]);

  return (
    <div className={`ld-screen${hidden ? " hidden" : ""}`}>
      <style>{css}</style>

      {/* video background */}
      <video
        ref={videoRef}
        className="ld-video"
        src={videoSrc || "/videos/intro.mp4"}
        autoPlay muted playsInline loop
      />

      {/* dark radial overlay */}
      <div className="ld-overlay" />

      {/* corner decorations */}
      <div className="ld-corner ld-corner--tl" />
      <div className="ld-corner ld-corner--tr" />
      <div className="ld-corner ld-corner--bl" />
      <div className="ld-corner ld-corner--br" />

      {/* particles */}
      <div className="ld-particles">
        {PARTICLES.map(p => (
          <div key={p.id} className="ld-dot" style={{
            left: p.left, top: p.top,
            "--d": p.d, "--delay": p.delay,
          }} />
        ))}
      </div>

      {/* main content */}
      <div className="ld-content">
        <div className="ld-ring">
          <div className="ld-ring__circle" />
          <div className="ld-ring__circle2" />
          <div className="ld-ring__inner">
            <div className="ld-ring__logo">ROEI<br />CAR</div>
          </div>
        </div>

        <div className="ld-brand">ROEI <span>CAR</span></div>
        <div className="ld-tagline">מתאים לך את הרכב הבא שלך!</div>

        <div className="ld-bar-wrap">
          <div className="ld-bar" style={{ width: `${progress}%` }} />
        </div>
        <div className="ld-pct">{progress}%</div>
      </div>
    </div>
  );
}